import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { KxRoiStrip } from './KxRoi';
import { HelpCircle } from 'lucide-react';
import { ModuleNotConnected, useRest, RCard, RHead, RStat, RestLoading, RestPageBrief, SourceBadge, money } from './restui';
import { TermHelp } from '../../components/AgentHelp';

/* Menu Intelligence — menu engineering from item-level sales + costing. */

const CLS = {
  star: { en: 'Star', es: 'Estrella', chip: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    desc_en: 'Popular AND high margin — your winning dish. Feature it everywhere.',
    desc_es: 'Popular Y con margen alto — tu plato ganador. Destácalo en todas partes.' },
  plowhorse: { en: 'Plowhorse', es: 'Popular', chip: 'bg-amber-50 border-amber-200 text-amber-700',
    desc_en: 'Sells a lot but leaves thin margin. Raise the price slightly or trim its cost.',
    desc_es: 'Se vende mucho pero deja poco margen. Sube un poco el precio o reduce su costo.' },
  puzzle: { en: 'Puzzle', es: 'Margen alto', chip: 'bg-sky-50 border-sky-200 text-sky-700',
    desc_en: 'Great margin but few sales. Promote it — every extra order is high profit.',
    desc_es: 'Gran margen pero pocas ventas. Promociónalo — cada orden extra es beneficio alto.' },
  dog: { en: 'Dog', es: 'Revisar', chip: 'bg-red-50 border-red-200 text-red-600',
    desc_en: 'Low sales and low margin. Candidate to reinvent or remove from the menu.',
    desc_es: 'Pocas ventas y poco margen. Candidato a reinventar o salir del menú.' },
};

export const DISH_IMG = {
  'Margherita Pizza': '/dishes/margherita-pizza.jpg',
  'Diavola Pizza': '/dishes/diavola-pizza.jpg',
  'Spaghetti Carbonara': '/dishes/spaghetti-carbonara.jpg',
  'Lasagna della Casa': '/dishes/lasagna-della-casa.jpg',
  'Burrata & Prosciutto': '/dishes/burrata-prosciutto.jpg',
  'Garlic Focaccia': '/dishes/garlic-focaccia.jpg',
  'Chicken Parmigiana': '/dishes/chicken-parmigiana.jpg',
  'Tiramisu': '/dishes/tiramisu.jpg',
  'House Chianti (glass)': '/dishes/house-chianti.jpg',
  'Aperol Spritz': '/dishes/aperol-spritz.jpg',
  'Tacos al Pastor': '/dishes/tacos-al-pastor.jpg',
  'Burrito Suprema': '/dishes/burrito-suprema.jpg',
  'Quesadilla Oaxaca': '/dishes/quesadilla-oaxaca.jpg',
  'Enchiladas Verdes': '/dishes/enchiladas-verdes.jpg',
  'Pozole Rojo': '/dishes/pozole-rojo.jpg',
  'Guacamole & Totopos': '/dishes/guacamole-totopos.jpg',
  'Torta Ahogada': '/dishes/torta-ahogada.jpg',
  'Churros con Cajeta': '/dishes/churros-cajeta.jpg',
  'Agua de Horchata': '/dishes/agua-horchata.jpg',
  'Margarita de la Casa': '/dishes/margarita-casa.jpg',
  'Latte': '/dishes/latte.jpg',
  'Cappuccino': '/dishes/cappuccino.jpg',
  'Cold Brew': '/dishes/cold-brew.jpg',
  'Matcha Latte': '/dishes/matcha-latte.jpg',
  'Avocado Toast': '/dishes/avocado-toast.jpg',
  'Breakfast Burrito': '/dishes/breakfast-burrito.jpg',
  'Granola Bowl': '/dishes/granola-bowl.jpg',
  'Butter Croissant': '/dishes/butter-croissant.jpg',
  'Blueberry Muffin': '/dishes/blueberry-muffin.jpg',
  'Chai Latte': '/dishes/chai-latte.jpg',
  'Arepa Reina Pepiada': '/dishes/arepa-reina-pepiada.jpg',
  'Arepa Pabellón': '/dishes/arepa-pabellon.jpg',
  'Arepa Pelúa': '/dishes/arepa-pelua.jpg',
  'Arepa Catira': '/dishes/arepa-catira.jpg',
  'Tequeños (x4)': '/dishes/tequenos.jpg',
  'Tostones (x3) Green Plantain': '/dishes/tostones-green-plantain.jpg',
  'Pabellón': '/dishes/pabellon-criollo.jpg',
  'Patacón Maracucho': '/dishes/patacon-maracucho.jpg',
  'Papelon Lemonade': '/dishes/papelon-lemonade.jpg',
  'Quesillo Venezuelan Flan': '/dishes/quesillo-flan.jpg',
  'Bife de Chorizo': '/dishes/bife-chorizo.jpg',
  'Ojo de Bife Madurado': '/dishes/ojo-bife.jpg',
  'Milanesa Hincha': '/dishes/milanesa-hincha.jpg',
  'Empanadas Criollas': '/dishes/empanadas-criollas.jpg',
  'Provoleta a la Brasa': '/dishes/provoleta.jpg',
  'Ñoquis de la Nonna': '/dishes/noquis-nonna.jpg',
  'Chimichurri Fries': '/dishes/chimichurri-fries.jpg',
  'Flan de Dulce de Leche': '/dishes/flan-ddl.jpg',
  'Malbec Reserva (copa)': '/dishes/malbec-glass.jpg',
  'Fernet con Coca': '/dishes/fernet-coca.jpg',
  'Milanesa Clásica': '/dishes/milanesa-clasica.jpg',
  'Milanesa Napolitana': '/dishes/milanesa-napolitana.jpg',
  'Milanesa a Caballo': '/dishes/milanesa-caballo.jpg',
  'Mila Sándwich Completo': '/dishes/mila-sandwich.jpg',
  'Chivito Uruguayo': '/dishes/chivito.jpg',
  'Papas Chalito': '/dishes/papas-chalito.jpg',
  'Ensalada Criolla': '/dishes/ensalada-criolla.jpg',
  'Postre Chajá': '/dishes/chaja.jpg',
  'Media y Media (copa)': '/dishes/media-media.jpg',
  'Limonada de la Casa': '/dishes/limonada-casa.jpg',
  'Mila Box Clásica': '/dishes/mila-box.jpg',
  'Mila Baguette': '/dishes/mila-baguette.jpg',
  'Mila Wrap': '/dishes/mila-wrap.jpg',
  'Mini Milas (6 pzs)': '/dishes/mini-milas.jpg',
  'Ensalada Mila': '/dishes/ensalada-mila.jpg',
  'Papas Rústicas': '/dishes/papas-rusticas.jpg',
  'Café Americano': '/dishes/cafe-americano.jpg',
  'Agua / Refresco': '/dishes/agua-refresco.jpg',
  'Brownie de Dulce de Leche': '/dishes/brownie-ddl.jpg',
  'Alfajor Artesanal': '/dishes/alfajor.jpg',
  'Omakase 12 Piezas': '/dishes/omakase.jpg',
  'Nigiri de Toro': '/dishes/nigiri-toro.jpg',
  'Nigiri de Salmón': '/dishes/nigiri-salmon.jpg',
  'Uramaki California': '/dishes/california-roll.jpg',
  'Uramaki Dragón': '/dishes/dragon-roll.jpg',
  'Sashimi Mixto': '/dishes/sashimi-mixto.jpg',
  'Gyozas de Pollo': '/dishes/gyozas.jpg',
  'Ramen Tonkotsu': '/dishes/ramen-tonkotsu.jpg',
  'Mochi Helado': '/dishes/mochi.jpg',
  'Sake Junmai (copa)': '/dishes/sake-junmai.jpg',
  'Buffalo Wings (12 pzs)': '/dishes/buffalo-wings.jpg',
  'BBQ Smokehouse Burger': '/dishes/bbq-smokehouse-burger.jpg',
  'Pepperoni Pan Pizza': '/dishes/pepperoni-pan-pizza.jpg',
  'Loaded Nachos': '/dishes/loaded-nachos.jpg',
  'Philly Cheesesteak': '/dishes/philly-cheesesteak.jpg',
  'Mozzarella Sticks': '/dishes/mozzarella-sticks.jpg',
  'BBQ Baby Back Ribs': '/dishes/bbq-baby-back-ribs.jpg',
  'Caesar Salad': '/dishes/caesar-salad.jpg',
  'Craft IPA (pinta)': '/dishes/craft-ipa.jpg',
  'Brownie Sundae': '/dishes/brownie-sundae.jpg',
  'Bandeja Paisa': '/dishes/bandeja-paisa.jpg',
  'Arroz Con Pollo': '/dishes/arroz-con-pollo.jpg',
  'Mojarra Frita': '/dishes/mojarra-frita.jpg',
  'Calentado': '/dishes/calentado.jpg',
  'Empanada': '/dishes/empanada-colombiana.jpg',
  'Chicharrón Con Arepa': '/dishes/chicharron-arepa.jpg',
  'Pandebono': '/dishes/pandebono.jpg',
  'Tres Leches': '/dishes/tres-leches.jpg',
  'Jugos Naturales': '/dishes/jugos-naturales.jpg',
  'Aguapanela Con Limón': '/dishes/aguapanela-limon.jpg',
  'Ceviche Clásico': '/dishes/ceviche-clasico.jpg',
  'Tiradito de Atún': '/dishes/tiradito-atun.jpg',
  'Pulpo a la Parrilla': '/dishes/pulpo-parrilla.jpg',
  'Lomo Saltado Clásico': '/dishes/lomo-saltado-andino.jpg',
  'Pollo a la Brasa': '/dishes/pollo-brasa.jpg',
  'Tacos de la Calle': '/dishes/tacos-calle.jpg',
  'Arroz con Mariscos': '/dishes/arroz-mariscos.jpg',
  'Suspiro Andino': '/dishes/suspiro-andino.jpg',
};

export const DISH_POOLS = {
  italian: ['Margherita Pizza', 'Diavola Pizza', 'Spaghetti Carbonara', 'Lasagna della Casa', 'Burrata & Prosciutto', 'Garlic Focaccia', 'Chicken Parmigiana', 'Tiramisu', 'House Chianti (glass)', 'Aperol Spritz'],
  mexican: ['Tacos al Pastor', 'Burrito Suprema', 'Quesadilla Oaxaca', 'Enchiladas Verdes', 'Pozole Rojo', 'Guacamole & Totopos', 'Torta Ahogada', 'Churros con Cajeta', 'Agua de Horchata', 'Margarita de la Casa'],
  cafe: ['Latte', 'Cappuccino', 'Cold Brew', 'Matcha Latte', 'Avocado Toast', 'Breakfast Burrito', 'Granola Bowl', 'Butter Croissant', 'Blueberry Muffin', 'Chai Latte'],
  arepas: ['Arepa Reina Pepiada', 'Arepa Pabellón', 'Arepa Pelúa', 'Arepa Catira', 'Tequeños (x4)', 'Tostones (x3) Green Plantain', 'Pabellón', 'Patacón Maracucho', 'Papelon Lemonade', 'Quesillo Venezuelan Flan'],
  argentinian: ['Bife de Chorizo', 'Ojo de Bife Madurado', 'Milanesa Hincha', 'Empanadas Criollas', 'Provoleta a la Brasa', 'Ñoquis de la Nonna', 'Chimichurri Fries', 'Flan de Dulce de Leche', 'Malbec Reserva (copa)', 'Fernet con Coca'],
  milanesas: ['Milanesa Clásica', 'Milanesa Napolitana', 'Milanesa a Caballo', 'Mila Sándwich Completo', 'Chivito Uruguayo', 'Papas Chalito', 'Ensalada Criolla', 'Postre Chajá', 'Media y Media (copa)', 'Limonada de la Casa'],
  grabgo: ['Mila Box Clásica', 'Mila Baguette', 'Mila Wrap', 'Mini Milas (6 pzs)', 'Ensalada Mila', 'Papas Rústicas', 'Café Americano', 'Agua / Refresco', 'Brownie de Dulce de Leche', 'Alfajor Artesanal'],
  sushi: ['Omakase 12 Piezas', 'Nigiri de Toro', 'Nigiri de Salmón', 'Uramaki California', 'Uramaki Dragón', 'Sashimi Mixto', 'Gyozas de Pollo', 'Ramen Tonkotsu', 'Mochi Helado', 'Sake Junmai (copa)'],
  colombian: ['Bandeja Paisa', 'Arroz Con Pollo', 'Mojarra Frita', 'Calentado', 'Empanada', 'Chicharrón Con Arepa', 'Pandebono', 'Tres Leches', 'Jugos Naturales', 'Aguapanela Con Limón'],
  andino: ['Ceviche Clásico', 'Tiradito de Atún', 'Pulpo a la Parrilla', 'Lomo Saltado Clásico', 'Pollo a la Brasa', 'Tacos de la Calle', 'Arroz con Mariscos', 'Suspiro Andino'],
  sportsbar: ['Buffalo Wings (12 pzs)', 'BBQ Smokehouse Burger', 'Pepperoni Pan Pizza', 'Loaded Nachos', 'Philly Cheesesteak', 'Mozzarella Sticks', 'BBQ Baby Back Ribs', 'Caesar Salad', 'Craft IPA (pinta)', 'Brownie Sundae'],
};

// The dish-of-the-day pool follows the restaurant's cuisine — a taquería never suggests a chai latte.
export function dishPoolFor(cuisine) {
  const c = (cuisine || '').toLowerCase();
  if (c.includes('peru') || c.includes('andin') || c.includes('fusion')) return DISH_POOLS.andino;
  if (c.includes('colomb') || c.includes('paisa')) return DISH_POOLS.colombian;
  if (c.includes('venez') || c.includes('arepa')) return DISH_POOLS.arepas;
  if (c.includes('argent') || c.includes('parrilla') || c.includes('steak')) return DISH_POOLS.argentinian;
  if (c.includes('urug') || c.includes('milanesa')) return DISH_POOLS.milanesas;
  if (c.includes('grab') || c.includes('airport') || c.includes('aero')) return DISH_POOLS.grabgo;
  if (c.includes('jap') || c.includes('sushi')) return DISH_POOLS.sushi;
  if (c.includes('sport') || c.includes('american') || c.includes('wings') || c.includes('bbq')) return DISH_POOLS.sportsbar;
  if (c.includes('mex') || c.includes('tac')) return DISH_POOLS.mexican;
  if (c.includes('caf') || c.includes('bak') || c.includes('coffee') || c.includes('brunch')) return DISH_POOLS.cafe;
  return DISH_POOLS.italian;
}

function ClsChip({ cls, isEs }) {
  const c = CLS[cls];
  if (!c) return null;
  return (
    <span title={isEs ? c.desc_es : c.desc_en}
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide cursor-help ${c.chip}`}>
      {isEs ? c.es : c.en}
    </span>
  );
}

export default function RestMenu() {
  const { isEs } = useOutletContext();
  const [days, setDays] = useState(30);
  const [showHelp, setShowHelp] = useState(false);
  const [clsFilter, setClsFilter] = useState(null);
  const { data: d, loading } = useRest(`/menu?days=${days}`);
  if (loading && !d) return <RestLoading />;
  if (!d?.has_data) {
    return <ModuleNotConnected isEs={isEs} title="Menu Intelligence" testid="rest-module-menu"
      desc_en="Best sellers, margins, food cost and menu engineering." desc_es="Best sellers, márgenes, food cost e ingeniería de menú."
      needs={['Toast API / item-level CSV', 'Food cost data', 'Menu prices']} />;
  }
  const s = d.summary;
  const q = s.quadrants || {};
  const dog = d.items.find((i) => i.cls === 'dog');
  const items = clsFilter ? d.items.filter((i) => i.cls === clsFilter) : d.items;
  return (
    <div data-testid="rest-menu">
      <RHead title="Menu Intelligence" sub={`${d.window.from} → ${d.window.to}`}
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="rest-menu-range"
              className="h-9 px-3 rounded-full border border-gray-200 bg-white text-[13px] font-semibold text-gray-600 focus:outline-none cursor-pointer">
              {[[30, isEs ? '30 días' : '30 days'], [90, isEs ? '90 días' : '90 days'], [365, isEs ? '12 meses' : '12 months']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <SourceBadge source="manual_imports" isEs={isEs} />
          </div>
        } />
      <KxRoiStrip isEs={isEs} />

      <RestPageBrief isEs={isEs} testid="rest-menu-brief"
        summary={isEs
          ? `Tu best seller es ${s.best_seller.name} — ${s.best_seller.units.toLocaleString()} vendidos (${money(s.best_seller.sales)}). Food cost promedio: ${s.avg_food_cost_pct}%. Tienes ${q.star || 0} platos Estrella y ${q.puzzle || 0} con margen alto por promocionar.`
          : `Your best seller is ${s.best_seller.name} — ${s.best_seller.units.toLocaleString()} sold (${money(s.best_seller.sales)}). Average food cost: ${s.avg_food_cost_pct}%. You have ${q.star || 0} Star dishes and ${q.puzzle || 0} high-margin ones worth promoting.`}
        alert={dog ? `${dog.name}: ${isEs ? dog.action_es : dog.action_en}` : null}
        kpi={{ label: <>{isEs ? 'Food cost promedio' : 'Avg food cost'} <TermHelp term="food_cost" isEs={isEs} /></>, value: `${s.avg_food_cost_pct}%`, sub: isEs ? 'objetivo ≤ 30%' : 'target ≤ 30%', down: s.avg_food_cost_pct > 30 }} />

      {(() => {
        const withImg = [...items].filter((i) => DISH_IMG[i.name]).sort((a, b) => b.units - a.units).slice(0, 8);
        if (!withImg.length) return null;
        return (
          <div className="mb-5" data-testid="menu-popular-dishes">
            <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-2.5">{isEs ? 'Platos populares' : 'Popular dishes'}</p>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
              {withImg.map((it) => (
                <div key={it.name} className="shrink-0 w-[150px] sm:w-[172px] snap-start rounded-2xl bg-white border border-gray-200 overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] duration-150"
                  data-testid={`popular-dish-${it.name.replace(/\s+/g, '-').toLowerCase()}`}>
                  <img src={DISH_IMG[it.name]} alt={it.name} loading="eager" className="w-full h-[104px] sm:h-[120px] object-cover" />
                  <div className="p-2.5">
                    <p className="text-[12px] font-bold text-gray-900 leading-tight truncate" title={it.name}>{it.name}</p>
                    <p className="mt-0.5 text-[11px] text-gray-500 tabular-nums">{it.units.toLocaleString()} {isEs ? 'vendidos' : 'sold'} · {money(it.sales)}</p>
                    <div className="mt-1.5"><ClsChip cls={it.cls} isEs={isEs} /></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <RStat label={isEs ? 'Ventas del periodo' : 'Period sales'} value={money(s.total_sales)} testid="menu-kpi-sales" />
        <RStat label={isEs ? 'Best seller' : 'Best seller'} value={s.best_seller.name} sub={`${s.best_seller.units.toLocaleString()} ${isEs ? 'vendidos' : 'sold'}`} testid="menu-kpi-best" />
        {s.top_margin && <RStat label={isEs ? 'Mejor margen' : 'Top margin'} value={s.top_margin.name} sub={`${s.top_margin.margin_pct}% ${isEs ? 'margen' : 'margin'}`} testid="menu-kpi-margin" />}
        <RStat label={isEs ? 'Platos estrella' : 'Star dishes'} value={q.star || 0} sub={isEs ? `${q.dog || 0} por revisar` : `${q.dog || 0} to review`} testid="menu-kpi-stars" />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="menu-quadrants">
        {Object.entries(CLS).map(([k, c]) => (
          <button key={k} title={isEs ? c.desc_es : c.desc_en} onClick={() => setClsFilter(clsFilter === k ? null : k)}
            data-testid={`menu-quadrant-${k}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-semibold cursor-pointer transition-all ${c.chip} ${clsFilter === k ? 'ring-2 ring-offset-1 ring-[var(--rest-accent)] shadow-sm' : clsFilter ? 'opacity-45 hover:opacity-80' : 'hover:shadow-sm'}`}>
            {isEs ? c.es : c.en} · {q[k] || 0}
          </button>
        ))}
        {clsFilter && (
          <button onClick={() => setClsFilter(null)} data-testid="menu-filter-clear"
            className="inline-flex items-center gap-1 rounded-full bg-gray-900 text-white px-3 py-1.5 text-[12px] font-semibold hover:bg-gray-700 transition-colors">
            ✕ {isEs ? `Mostrando solo ${CLS[clsFilter][isEs ? 'es' : 'en']} — quitar filtro` : `Showing only ${CLS[clsFilter].en} — clear filter`}
          </button>
        )}
        <button onClick={() => setShowHelp(!showHelp)} data-testid="menu-class-help"
          className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-gray-500 hover:border-[var(--rest-accent)] hover:text-[var(--rest-accent)] transition-colors">
          <HelpCircle className="w-3.5 h-3.5" /> {isEs ? '¿Qué significan?' : 'What do these mean?'}
        </button>
      </div>
      {showHelp && (
        <RCard className="p-4 sm:p-5 mb-4" testid="menu-class-help-panel">
          <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-3">{isEs ? 'Ingeniería de menú — las 4 clases' : 'Menu engineering — the 4 classes'}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {Object.entries(CLS).map(([k, c]) => (
              <div key={k} className="flex items-start gap-2.5" data-testid={`menu-class-desc-${k}`}>
                <span className={`mt-0.5 inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide ${c.chip}`}>{isEs ? c.es : c.en}</span>
                <p className="text-[12.5px] text-gray-600 leading-snug">{isEs ? c.desc_es : c.desc_en}</p>
              </div>
            ))}
          </div>
        </RCard>
      )}

      <RCard className="overflow-hidden" testid="rest-menu-table">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-[10.5px] uppercase tracking-wider text-gray-400">
                {[isEs ? 'Plato' : 'Dish', isEs ? 'Precio' : 'Price', 'Food cost', isEs ? 'Margen' : 'Margin', isEs ? 'Vendidos' : 'Sold', isEs ? 'Ventas' : 'Sales', isEs ? 'Clase' : 'Class'].map((h, i) => (
                  <th key={i} className={`px-4 lg:px-6 py-3 font-bold ${i > 0 && i < 6 ? 'text-right' : ''}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.name} className="border-t border-gray-50 hover:bg-orange-50/40 transition-colors" data-testid={`menu-item-${it.name.replace(/\s+/g, '-').toLowerCase()}`}>
                  <td className="px-4 lg:px-6 py-3.5">
                    <p className="font-semibold text-gray-900">{it.name} <span className="ml-1 text-[10.5px] uppercase font-bold text-gray-300">{it.category}</span></p>
                    {it.cls && <p className="text-[11px] text-gray-400 mt-0.5">{isEs ? it.action_es : it.action_en}</p>}
                  </td>
                  <td className="px-4 lg:px-6 py-3.5 text-right tabular-nums font-semibold text-gray-900">${it.price.toFixed(2)}</td>
                  <td className="px-4 lg:px-6 py-3.5 text-right tabular-nums text-gray-600">{it.food_cost != null ? <>${it.food_cost.toFixed(2)} <span className="text-gray-400">· {it.cost_pct}%</span></> : '—'}</td>
                  <td className="px-4 lg:px-6 py-3.5 text-right tabular-nums text-gray-600">{it.margin != null ? <>${it.margin.toFixed(2)} <span className="text-gray-400">· {it.margin_pct}%</span></> : '—'}</td>
                  <td className="px-4 lg:px-6 py-3.5 text-right tabular-nums text-gray-600">{it.units.toLocaleString()}</td>
                  <td className="px-4 lg:px-6 py-3.5 text-right tabular-nums font-bold text-gray-900">{money(it.sales)}</td>
                  <td className="px-4 lg:px-6 py-3.5"><ClsChip cls={it.cls} isEs={isEs} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:hidden divide-y divide-gray-50">
          {items.map((it) => (
            <div key={it.name} className="px-4 py-3.5" data-testid={`menu-item-m-${it.name.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-gray-900 text-[13.5px]">{it.name}</p>
                <ClsChip cls={it.cls} isEs={isEs} />
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-gray-500 tabular-nums">
                <b className="text-gray-900 text-[13px]">{money(it.sales)}</b>
                <span>${it.price.toFixed(2)}</span>
                {it.cost_pct != null && <span>FC {it.cost_pct}%</span>}
                {it.margin_pct != null && <span>{isEs ? 'margen' : 'margin'} {it.margin_pct}%</span>}
                <span>{it.units.toLocaleString()} {isEs ? 'vend.' : 'sold'}</span>
              </div>
              {it.cls && <p className="mt-1 text-[11px] text-gray-400">{isEs ? it.action_es : it.action_en}</p>}
            </div>
          ))}
        </div>
      </RCard>
      <p className="mt-3 text-[11px] text-gray-400">
        {isEs ? 'Costeo aproximado por plato (configurable) · Ingeniería de menú: Estrella = popular + margen alto · Popular = popular, poco margen · Margen alto = poco vendido · Revisar = ninguno de los dos.'
          : 'Approximate per-dish costing (configurable) · Menu engineering: Star = popular + high margin · Plowhorse = popular, thin margin · Puzzle = high margin, low sales · Dog = neither.'}
      </p>
    </div>
  );
}
